<template>
  <div class='m-4 grid grid-cols-1 gap-x-20 gap-y-4'>
    <DividerComponent name='Two-Factor Authentication Setup'/>
    <div class='grid grid-cols-4 gap-x-20 gap-y-4'>
      <div class='col-span-4'>
        <div class=''>
          <div class='text-xl font-semibold text-gray-500'>
            <h2 class='text-center'>Verification methods</h2>
          </div>
          <div
            v-if='availableMethods.length'
            class='mt-6 grid gap-6'
            :style='columnsGridStyle'
          >
            <div
              v-for='method in availableMethods'
              :key='method.methodType'
              class='rounded-lg border border-gray-200 bg-white shadow-sm'
            >
              <div class='flex  items-center justify-between border-b border-gray-100 px-4 py-3'>
                <div>
                  <div class='text-xs uppercase tracking-wide text-gray-500'>{{ method.methodType }}</div>
                </div>
                <button
                  v-if='canAddMethod(method.methodType)'
                  type='button'
                  class='rounded-md bg-gray-700 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-gray-600'
                  @click='triggerAddMethod(method.methodType)'
                >
                  Add
                </button>
              </div>
              <div class='p-4'>
                <div
                  v-if='getMethodsByType(method.methodType).length'
                  class='space-y-2'
                >
                  <div
                    v-for='existingMethod in getMethodsByType(method.methodType)'
                    :key='existingMethod.methodType + existingMethod.methodName'
                    class='rounded border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700'
                  >
                    <div class='flex flex-row items-center justify-between'>
                      <div class='font-medium text-gray-900'>{{ existingMethod.methodName }}</div>
                      <button
                        type='button'
                        class='rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-red-500'
                        @click='removeMethod(existingMethod)'
                      >
                        Delete
                      </button>
                    </div>
                    <div class='mt-3 grid gap-2 border-t border-gray-200 pt-3'>
                      <label
                        v-for='action in preferenceActions'
                        :key='`${existingMethod.methodType}-${existingMethod.methodName}-${action.value}`'
                        class='flex items-center justify-between gap-3 text-xs text-gray-600'
                      >
                        <span>{{ action.label }}</span>
                        <input
                          type='checkbox'
                          class='h-4 w-4 rounded border-gray-300 text-gray-700 focus:ring-gray-500'
                          :checked='isPreferredForAction(existingMethod, action.value)'
                          :disabled='updatingPreference === preferenceKey(existingMethod, action.value)'
                          @change='updateMethodPreference(existingMethod, action.value, $event)'
                        >
                      </label>
                    </div>
                  </div>
                </div>
                <p
                  v-if='isPasskeyMethod(method.methodType) && !hasPasskeys'
                  class='text-center text-sm text-gray-500'
                >
                  No passkeys configured.
                </p>
                <p
                  v-else-if='!getMethodsByType(method.methodType).length'
                  class='text-center text-sm text-gray-500'
                >
                  No methods configured.
                </p>
              </div>
            </div>
          </div>
          <p
            v-else
            class='mt-4 text-center text-sm text-gray-500'
          >
            No verification methods available.
          </p>
        </div>
      </div>
    </div>
    <div>
      <Modal
        :open='createModalOpen'
        title='Add verification method'
        modal-classes='w-full max-w-2xl p-0 absolute'
        title-classes='text-left border-b px-6 pt-5 pb-4'
        content-classes='flex flex-col gap-4 px-6 py-6 bg-gray-50'
        @close-modal='closeCreateModal'
      >
        <template #content>
          <div class='rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
            <div class='flex flex-col gap-2'>
              <div class='text-xs uppercase tracking-wide text-gray-500'>{{ selectedMethodType?.methodType || 'Verification method' }}</div>
              <div class='text-base font-semibold text-gray-900'>{{ selectedMethodType?.methodName || 'New verification method' }}</div>
            </div>
            <div class='mt-6 flex flex-col gap-4'>
              <div class='w-full'>
                <StringField
                  v-model='methodNameInput'
                  name='methodName'
                  label='Method name'
                  :field='{ mandatory: true }'
                  :edit-mode='true'
                  :error='nameError'
                  placeholder='Enter method name'
                />
              </div>
              <button
                type='button'
                class='h-10 rounded border border-transparent bg-gray-700 px-4 text-sm font-semibold text-white transition hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-60 sm:self-end'
                :disabled='!canSubmit || !selectedMethodType'
                @click='handleSubmit'
              >
                {{ submitting ? 'Submitting...' : 'Submit' }}
              </button>
            </div>
          </div>
          <div
            v-if='!selectedMethodType'
            class='rounded border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500'
          >
            Select a verification method to configure.
          </div>
        </template>
      </Modal>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import DividerComponent from '@/components/partials/DividerComponent.vue'
  import { useVerificationStore } from '@/store/verification'
  import type { VerificationActionMethodResult, VerificationChallengeSummary, VerificationMethod } from '@/api/requests/verification/verificationResponses'
  import Modal from '@/components/Modal.vue'
  import { StringField } from '@/components/inputs'
  import { closeModal } from '@/utils/modalController'
  import router from '@/router'
  import { assertWebAuthnSupported,
           preparePublicKeyRequestOptions,
           publicKeyCredentialToPayload } from '@/utils/webauthn'
  import { getPasskeys } from '@/api/requests/user/passkeys'
  import { useAppStore } from '@/store/app'
  import type { Passkey } from '@/api/requests/user/passkeys'

  const verificationStore = useVerificationStore()
  const appStore = useAppStore()
  const passkeysEnabled = computed(() => appStore.arePasskeysEnabled)
  const verificationMethods = computed(() => verificationStore.verificationMethods)
  const availableMethods = computed(() => verificationStore.availableMethods
    .filter((method) => passkeysEnabled.value || !isPasskeyMethod(method.methodType)))
  const passkeys = ref<Passkey[]>([])
  const hasPasskeys = computed(() => passkeys.value.length > 0)

  const columnsGridStyle = computed(() => {
    const count = availableMethods.value.length || 1

    return {
      gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
    }
  })

  const methodNameInput = ref('')
  const submitting = ref(false)
  const nameError = ref<string[] | null>(null)
  const canSubmit = computed(() => methodNameInput.value.trim().length > 0 && !submitting.value)

  const createModalOpen = ref(false)
  const selectedMethodType = ref<VerificationMethod | null>(null)
  const updatingPreference = ref<string | null>(null)
  const passkeySetupInProgress = ref(false)
  const preferenceActions = [
    {
      value: 'force-verification',
      label: 'CRM session checks',
    },
    {
      value: '2FA-crm',
      label: 'Sensitive CRM actions',
    },
  ]

  const getMethodsByType = (methodType: string) => {
    const methods = Array.isArray(verificationMethods.value) ? verificationMethods.value : []
    
    return methods.filter((method) => method.methodType === methodType)
  }

  const resetState = () => {
    methodNameInput.value = ''
    submitting.value = false
    nameError.value = null
    createModalOpen.value = false
    selectedMethodType.value = null
    verificationStore.selectedMethodType = null
    verificationStore.selectedMethodName = null
  }

  const triggerAddMethod = (methodType: string) => {
    if (!canAddMethod(methodType)) {
      return
    }

    selectedMethodType.value = availableMethods.value.find((item) => item.methodType === methodType) ?? null
    verificationStore.selectedMethodType = selectedMethodType.value?.methodType ?? null
    verificationStore.selectedMethodName = selectedMethodType.value?.methodName ?? null
    methodNameInput.value = selectedMethodType.value?.methodName ?? ''
    nameError.value = null
    createModalOpen.value = true
  }

  const closeCreateModal = () => {
    createModalOpen.value = false
    selectedMethodType.value = null
    methodNameInput.value = ''
    nameError.value = null
  }

  const removeMethod = async (method: VerificationMethod) => {
    await verificationStore.removeUserVerifiableMethod({
      methodName: method.methodName,
      methodType: method.methodType,
      verificationUuid: verificationStore.verificationUuid,
      answer: '',
    })
    await verificationStore.loadVerifiableMethods()
  }

  const preferenceKey = (method: VerificationMethod, action: string) => `${method.methodType}:${method.methodName}:${action}`

  const isPreferredForAction = (method: VerificationMethod, action: string) => {
    return Array.isArray(method.preferredForActions) && method.preferredForActions.includes(action)
  }

  const updateMethodPreference = async (method: VerificationMethod, action: string, event: Event) => {
    const enabled = event.target instanceof HTMLInputElement && event.target.checked
    const nextActions = new Set(method.preferredForActions ?? [])

    if (enabled) {
      nextActions.add(action)
    } else {
      nextActions.delete(action)
    }

    updatingPreference.value = preferenceKey(method, action)

    try {
      await verificationStore.updateUserVerifiableMethod({
        methodName: method.methodName,
        methodType: method.methodType,
        preferredForActions: Array.from(nextActions),
      })
      await verificationStore.loadVerifiableMethods()
    } finally {
      updatingPreference.value = null
    }
  }

  const handleSubmit = async () => {
    if (!selectedMethodType.value || !canSubmit.value) {
      nameError.value = ['Name is required']
      
      return
    }

    submitting.value = true
    nameError.value = null

    try {
      if (isPasskeyMethod(selectedMethodType.value.methodType)) {
        await configurePasskeyMethod(selectedMethodType.value)
      } else {
        await verificationStore.addUserVerifiableMethod({
          methodName: methodNameInput.value.trim(),
          methodType: selectedMethodType.value.methodType,
          verificationUuid: verificationStore.verificationUuid,
        })
      }

      closeCreateModal()

      await verificationStore.loadVerifiableMethods()
      await loadPasskeys()
    } catch (error) {
      nameError.value = ['Unable to add method']
    } finally {
      if (!verificationStore.requiresVerification) {
        closeModal()
      }
      submitting.value = false
    }
  }

  const isPasskeyMethod = (methodType: string) => methodType.toLowerCase() === 'passkey'
  const canAddMethod = (methodType: string) => !isPasskeyMethod(methodType) || (passkeysEnabled.value && hasPasskeys.value)

  const loadPasskeys = async () => {
    if (!passkeysEnabled.value) {
      passkeys.value = []

      return
    }

    try {
      const response = await getPasskeys()

      passkeys.value = response.data.data ?? []
    } catch {
      passkeys.value = []
    }
  }

  const configurePasskeyMethod = async (method: VerificationMethod) => {
    passkeySetupInProgress.value = true
    const methodName = methodNameInput.value.trim()

    try {
      const response = await verificationStore.addUserVerifiableMethod({
        methodName,
        methodType: method.methodType,
        verificationUuid: verificationStore.getVerificationUuid(),
      }, false)

      const summary = response.data as VerificationChallengeSummary
      const methodResult = passkeyMethodResult(summary)
      const answer = await passkeyAnswer(methodResult)

      await verificationStore.addUserVerifiableMethod({
        methodName,
        methodType: method.methodType,
        verificationUuid: summary.verificationUuid,
        answer,
      }, false)
    } finally {
      passkeySetupInProgress.value = false
    }
  }

  const passkeyMethodResult = (summary: VerificationChallengeSummary): VerificationActionMethodResult => {
    const methodResult = summary.actionResults
      ?.find((result) => result.action === 'configure')
      ?.methodResults
      ?.find((result) => result.methodType.toLowerCase() === 'passkey')

    if (!methodResult) {
      throw new Error('Passkey challenge is missing.')
    }

    return methodResult
  }

  const passkeyAnswer = async (methodResult: VerificationActionMethodResult): Promise<string> => {
    assertWebAuthnSupported()

    const data = methodResult.data ?? {
    }
    const challengeId = data.challenge_id ?? data.challengeId
    const options = data.options

    if (typeof challengeId !== 'string' || !options || typeof options !== 'object') {
      throw new Error('Passkey challenge is missing.')
    }

    const credential = await navigator.credentials.get({
      publicKey: preparePublicKeyRequestOptions(options as Parameters<typeof preparePublicKeyRequestOptions>[0]),
    })

    if (!credential) {
      throw new Error('Passkey verification was cancelled.')
    }

    return JSON.stringify({
      challenge_id: challengeId,
      passkey: publicKeyCredentialToPayload(credential),
    })
  }

  watch(passkeysEnabled, (enabled) => {
    if (enabled) {
      loadPasskeys()
    }
  })

  watch(methodNameInput, (value) => {
    if (!createModalOpen.value) {
      return
    }

    verificationStore.selectedMethodName = value
  })

  watch(() => verificationStore.requiresVerification, (requiresVerification) => {
    if (passkeySetupInProgress.value) {
      return
    }

    if (!requiresVerification) {
      closeModal()
      router.go(0)
    }

    closeCreateModal()
  })

  onMounted(() => {
    appStore.loadAppConfig()
    verificationStore.loadVerifiableMethods()
    verificationStore.loadAvailableVerificationMethods()
    loadPasskeys()
  })

  onBeforeUnmount(() => {
    resetState()
  })

</script>
